import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { DollarSign, Package, Percent, Landmark, ArrowLeft } from "lucide-react";

export default function Earnings() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/payouts/earnings");
      setData(data);
    })();
  }, []);

  if (!data) return <div className="p-10 text-neutral-500">Loading…</div>;
  const s = data.summary;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="earnings-page">
      <Link to="/seller" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>
      <div className="mt-4 text-xs uppercase tracking-widest text-[#007AFF]">Earnings</div>
      <h1 className="mt-2 font-black tracking-tight text-4xl">Your earnings recap</h1>
      <p className="mt-2 text-sm text-neutral-400">
        Gross sales, Vismarks fees deducted, and what settles to your account.
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="earnings-summary">
        <Tile icon={DollarSign} label="Gross sales" value={`$${s.gross_usd.toLocaleString()}`} />
        <Tile icon={Package} label="Units sold" value={s.units} />
        <Tile icon={Percent} label="Vismarks fees" value={`−$${s.fees_usd.toLocaleString()}`} accent="text-[#FF9F0A]" />
        <Tile icon={Landmark} label="Net to you" value={`$${s.net_usd.toLocaleString()}`} accent="text-[#34C759]" />
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div data-testid="earnings-settled-card" className="rounded-xl border border-[#34C759]/30 bg-[#34C759]/[0.05] p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#34C759]">Settled directly to your account</div>
          <div className="mt-2 font-mono tabular-nums font-black text-3xl">${s.settled_direct_usd.toLocaleString()}</div>
          <p className="mt-2 text-xs text-neutral-500">
            Orders paid through your connected Paystack bank or Stripe account — fees were deducted upfront.
          </p>
        </div>
        <div data-testid="earnings-pending-card" className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Pending manual settlement</div>
          <div className="mt-2 font-mono tabular-nums font-black text-3xl">${s.pending_settlement_usd.toLocaleString()}</div>
          <p className="mt-2 text-xs text-neutral-500">
            Older orders processed before your payment channel was connected — settled by the platform.
          </p>
        </div>
      </div>

      {data.recent.length > 0 ? (
        <div className="mt-10">
          <h2 className="font-black text-xl tracking-tight mb-3">Recent orders</h2>
          <div className="rounded-xl border border-[#27272A] overflow-x-auto">
            <table className="w-full text-sm" data-testid="earnings-orders-table">
              <thead className="bg-[#0A0A0A] text-xs uppercase tracking-widest text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-3">Product</th>
                  <th className="text-right px-4 py-3">Qty</th>
                  <th className="text-right px-4 py-3">Gross</th>
                  <th className="text-right px-4 py-3">Fee</th>
                  <th className="text-right px-4 py-3">Net</th>
                  <th className="text-left px-4 py-3">Via</th>
                  <th className="text-left px-4 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((o) => (
                  <tr key={o.id} className="border-t border-[#27272A]">
                    <td className="px-4 py-3 text-white">{o.product_title}</td>
                    <td className="px-4 py-3 text-right font-mono">{o.quantity}</td>
                    <td className="px-4 py-3 text-right font-mono">${o.total_usd.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#FF9F0A]">−${o.fee_usd.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#34C759]">${o.net_usd.toFixed(2)}</td>
                    <td className="px-4 py-3 text-neutral-400 capitalize">{o.provider}</td>
                    <td className="px-4 py-3 text-neutral-500">{new Date(o.placed_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="mt-10 rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center text-neutral-500 text-sm" data-testid="earnings-empty">
          No completed orders yet — your earnings recap will appear here after your first sale.
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#121212] p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
        <Icon className="h-4 w-4 text-neutral-600" />
      </div>
      <div className={`mt-3 font-mono tabular-nums font-bold text-2xl ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}
